import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Link2, Mail, Calendar, Trash2, RefreshCw, Shield, 
  CheckCircle, AlertTriangle, ExternalLink, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

export default function ConnectedAccountsPage() {
  const { user, isAssistant } = useOutletContext();
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [calendarAccounts, setCalendarAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);

    try {
      const [emailRes, calendarRes] = await Promise.all([
        fetch(`${API_URL}/api/email/accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/calendar/accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (emailRes.ok) {
        setEmailAccounts(await emailRes.json());
      }
      if (calendarRes.ok) {
        setCalendarAccounts(await calendarRes.json());
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeEmail = async (accountId) => {
    if (!confirm('Êtes-vous sûr de vouloir déconnecter ce compte email ?')) return;
    
    const token = localStorage.getItem('client_token');
    setRevoking(accountId);

    try {
      const response = await fetch(`${API_URL}/api/email/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Compte email déconnecté');
        setEmailAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeCalendar = async (accountId) => {
    if (!confirm('Êtes-vous sûr de vouloir déconnecter ce calendrier ?')) return;
    
    const token = localStorage.getItem('client_token');
    setRevoking(accountId);

    try {
      const response = await fetch(`${API_URL}/api/calendar/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Calendrier déconnecté');
        setCalendarAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setRevoking(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Inconnue';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProviderIcon = (provider) => {
    if (provider === 'gmail' || provider === 'google') {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      );
    }
    return <Mail className="w-5 h-5 text-slate-500" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
          <CheckCircle className="w-3 h-3" />
          Actif
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
        <AlertTriangle className="w-3 h-3" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  const hasNoAccounts = emailAccounts.length === 0 && calendarAccounts.length === 0;

  return (
    <div className="space-y-6" data-testid="connected-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link2 className="w-6 h-6 text-rm-primary" />
          <div>
            <h1 className="text-xl font-bold text-rm-text">Comptes connectés</h1>
            <p className="text-sm text-slate-500">Gère tes connexions Google et autres services</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAccounts}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Sécurité de tes données</h3>
            <p className="text-sm text-blue-700 mt-1">
              Vos identifiants sont stockés de manière sécurisée et chiffrée. 
              Vous pouvez révoquer l'accès à tout moment. La révocation supprime 
              immédiatement les tokens d'accès de notre système.
            </p>
          </div>
        </div>
      </div>

      {/* No Accounts */}
      {hasNoAccounts && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Link2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-rm-text mb-2">
            Aucun compte connecté
          </h2>
          <p className="text-slate-500 mb-4 max-w-md mx-auto">
            Connecte ton compte Google pour synchroniser tes emails et ton calendrier 
            avec Cockpit Pro.
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => window.location.href = '/espace-client/emails'}>
              <Mail className="w-4 h-4 mr-2" />
              Connecter Email
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/espace-client/shared-calendar'}>
              <Calendar className="w-4 h-4 mr-2" />
              Connecter Calendrier
            </Button>
          </div>
        </div>
      )}

      {/* Email Accounts */}
      {emailAccounts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Mail className="w-5 h-5 text-rm-primary" />
            <h2 className="font-semibold text-rm-text">Comptes Email</h2>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {emailAccounts.length}
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {emailAccounts.map(account => (
              <motion.div
                key={account.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    {getProviderIcon(account.provider)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-rm-text">{account.email_address}</p>
                      {getStatusBadge(account.status)}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {account.provider === 'gmail' ? 'Gmail (Google OAuth)' : 'IMAP/SMTP'}
                      {' • '}
                      Connecté le {formatDate(account.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {account.provider === 'gmail' && (
                    <a
                      href="https://myaccount.google.com/permissions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      Gérer sur Google
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {!isAssistant && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleRevokeEmail(account.id)}
                      disabled={revoking === account.id}
                    >
                      {revoking === account.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-600" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Révoquer
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Accounts */}
      {calendarAccounts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-rm-primary" />
            <h2 className="font-semibold text-rm-text">Calendriers</h2>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {calendarAccounts.length}
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {calendarAccounts.map(account => (
              <motion.div
                key={account.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    {getProviderIcon(account.provider)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-rm-text">{account.name}</p>
                      {getStatusBadge(account.status)}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {account.provider === 'google' ? 'Google Calendar (OAuth)' : 'CalDAV'}
                      {' • '}
                      Connecté le {formatDate(account.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {account.provider === 'google' && (
                    <a
                      href="https://myaccount.google.com/permissions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      Gérer sur Google
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {!isAssistant && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleRevokeCalendar(account.id)}
                      disabled={revoking === account.id}
                    >
                      {revoking === account.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-600" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Révoquer
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
        <h3 className="font-medium text-rm-text mb-2 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Besoin d'aide ?
        </h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• <strong>Révoquer</strong> supprime l'accès de Cockpit Pro à ton compte</li>
          <li>• Pour les comptes Google, vous pouvez aussi révoquer l'accès directement depuis votre compte Google</li>
          <li>• La révocation n'affecte pas tes données existantes dans Cockpit Pro</li>
          <li>• Vous pouvez reconnecter un compte à tout moment</li>
        </ul>
      </div>
    </div>
  );
}
